import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";

interface BillFileUploadProps {
  type: 'gas' | 'electricity' | 'water';
  handleFileUpload: (file: File, type: 'gas' | 'electricity' | 'water') => Promise<string | undefined>;
}

export const BillFileUpload = ({ type, handleFileUpload }: BillFileUploadProps) => {
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }

    const file = e.target.files[0];
    const fileSize = file.size / 1024 / 1024; // Convert to MB
    
    if (fileSize > 5) {
      toast({
        title: "File too large",
        description: "Please upload a file smaller than 5MB",
        variant: "destructive",
      });
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload a PDF, JPEG, or PNG file",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    try {
      const result = await handleFileUpload(file, type);
      if (!result) {
        throw new Error('Upload failed');
      }
      toast({
        title: "Success",
        description: "Your bill has been successfully uploaded",
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: "Upload failed",
        description: "There was an error uploading your file. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Input
      type="file"
      className="bg-[#F9FAFB]"
      onChange={handleFileChange}
      accept=".pdf,.jpg,.jpeg,.png"
      disabled={isUploading}
    />
  );
};